// Display helpers. Dates are shown in the `reporting_timezone` setting (default Europe/Sofia);
// the AppData provider calls setReportingTz() once settings load.

let tz = 'Europe/Sofia';
const cache = new Map<string, Intl.DateTimeFormat>();

export function setReportingTz(value: string) {
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: value });
    tz = value;
    cache.clear();
  } catch {
    /* unknown zone: keep the previous one */
  }
}

export function reportingTz() {
  return tz;
}

function fmt(kind: 'datetime' | 'date' | 'time') {
  const key = `${kind}|${tz}`;
  let f = cache.get(key);
  if (!f) {
    const opts: Intl.DateTimeFormatOptions =
      kind === 'date'
        ? { timeZone: tz, day: '2-digit', month: 'short', year: 'numeric' }
        : kind === 'time'
          ? { timeZone: tz, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }
          : { timeZone: tz, day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' };
    f = new Intl.DateTimeFormat('en-GB', opts);
    cache.set(key, f);
  }
  return f;
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : fmt('datetime').format(d);
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : fmt('date').format(d);
}

export function ago(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return 'never';
  const s = Math.round((now - new Date(iso).getTime()) / 1000);
  const future = s < 0;
  const a = Math.abs(s);
  const txt =
    a < 60 ? `${a}s` : a < 3600 ? `${Math.round(a / 60)} min` : a < 86400 ? `${Math.round(a / 3600)} h` : `${Math.round(a / 86400)} d`;
  return future ? `in ${txt}` : `${txt} ago`;
}

export const INTEREST_LABEL: Record<string, string> = {
  unreviewed: 'Unreviewed',
  interesting: 'Interesting',
  watch_later: 'Watch later',
  dismissed: 'Dismissed',
};

export const DEPTH_LABEL: Record<string, string> = { light: 'Light', deep: 'Deep' };

export const COMPLETENESS_LABEL: Record<string, string> = {
  complete: 'Complete',
  partial: 'Partial',
  minimal: 'Minimal',
};

export const PRICING_LABEL: Record<string, string> = {
  not_checked: 'Not checked',
  no_evidence_found: 'No evidence found',
  available: 'Evidence found',
};

export const JOB_TYPE_LABEL: Record<string, string> = {
  refresh_light_profile: 'Website refresh (Light)',
  check_info: 'Check info (missing information)',
  collect_deep_sources: 'Deep collection',
  discover_country: 'Discovery',
  extract_quotation: 'Quotation extraction',
  collect_feed: 'Channel collection (feed)',
  collect_history: 'Look back in time (feed history)',
  compile_weekly_report: 'Weekly report',
  analyze_positioning: 'Positioning analysis (model)',
  analyze_social: 'Post types and tone (model)',
  suggest_report_actions: 'Suggested actions (model)',
  research_prospect: 'Prospect website research',
  draft_outreach: 'Outreach draft (model)',
  draft_publication_text: 'Publication text suggestion (model)',
};

export const STATUS_LABEL: Record<string, string> = {
  queued: 'Queued',
  running: 'Running',
  waiting_review: 'Waiting for review',
  succeeded: 'Succeeded',
  partial: 'Partial',
  failed: 'Failed',
  cancelled: 'Cancelled',
  active: 'Active',
  paused: 'Paused',
  ended: 'Ended',
};

export const ACTION_LABEL: Record<string, string> = {
  'studio.create': 'Studio added',
  'studio.merge': 'Studios merged',
  'studio.promote': 'Promoted to Deep',
  'studio.demote': 'Returned to Light',
  'fact.accept': 'Proposed fact accepted',
  'import.run': 'Import ran',
};

export const FIELD_LABEL: Record<string, string> = {
  name: 'Name',
  website_url: 'Website',
  country: 'HQ country',
  city: 'HQ city',
  markets_served: 'Markets served',
  services: 'Services',
  client_types: 'Client types',
  team_size_band: 'Team size',
  team_size_source: 'Team size source',
  team_size_confidence: 'Team size confidence',
  logo: 'Logo',
  company_record: 'Official company record',
  'company_id:bg_eik': 'Bulgarian UIC (ЕИК)',
  'company_id:vat': 'EU VAT number',
  'company_id:fr_siren': 'French SIREN',
  'company_id:uk_company': 'UK company number',
  summary: 'Summary',
  pricing_evidence: 'Pricing evidence',
  last_activity_at: 'Last activity',
  last_activity_note: 'Last activity note',
};

export const TERMINAL: string[] = ['succeeded', 'partial', 'failed', 'cancelled'];

export function jobType(t: string) {
  return JOB_TYPE_LABEL[t] ?? t;
}

/** Plain-language reading of the collection errors written by app/egress.py and
 *  app/services/collect.py; null when the error is not one of them (show the raw text). */
export function explainJobError(error: string | null | undefined): string | null {
  if (!error) return null;
  if (/DNS lookup (failed|for .* returned no address)/.test(error)) return 'The website address does not exist (DNS lookup failed). Check the domain on the studio.';
  if (/robots\.txt (unreachable|HTTP 5)/.test(error)) return 'The site’s robots.txt could not be read, so no page was fetched.';
  if (/disallowed by robots\.txt/.test(error)) return 'The site’s robots.txt does not allow this page to be collected.';
  if (/resolves to non-public address/.test(error)) return 'The address points to a private network; it was not fetched.';
  if (/HTTP 403\b/.test(error)) return 'The website refused the visit (HTTP 403). Some sites block automated visits.';
  if (/HTTP 404\b/.test(error)) return 'The page was not found (HTTP 404).';
  if (/HTTP 5\d\d\b/.test(error)) return 'The website had a server error. It may work on the next try.';
  if (/Timeout|timed out/i.test(error)) return 'The website did not answer in time.';
  return null;
}

export function countryName(code: string | null | undefined): string {
  if (!code) return '—';
  try {
    const dn = new Intl.DisplayNames(['en'], { type: 'region' });
    return dn.of(code) ?? code;
  } catch {
    return code;
  }
}

export function plural(n: number, one: string, many = `${one}s`) {
  return `${n} ${n === 1 ? one : many}`;
}

export function fieldValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}
